import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, CheckCircle2, XCircle, RefreshCw, Send, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// PDF download will use server-side endpoint

const STATUS_LABEL = {
  draft: { label: "Draft", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  pending_approval: { label: "Pending approval", cls: "bg-amber-100 text-amber-900 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-900 border-red-200" },
  changes_requested: { label: "Changes requested", cls: "bg-orange-100 text-orange-900 border-orange-200" },
  sent: { label: "Sent", cls: "bg-sky-100 text-sky-900 border-sky-200" },
  paid: { label: "Paid", cls: "bg-emerald-200 text-emerald-900 border-emerald-300" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const [inv, setInv] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [payment, setPayment] = useState({ amount: "", method: "Bank Transfer", reference: "", payment_date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
    setPayment((p) => ({ ...p, amount: (data.total - data.amount_paid).toFixed(2) }));
  };
  useEffect(() => { load(); }, [id]);

  if (!inv) return <div className="text-sm text-zinc-500">Loading…</div>;

  const symbol = company?.currency_symbol || "₹";
  const isAdmin = user.role === "admin";
  const meta = STATUS_LABEL[inv.status] || { label: inv.status, cls: "" };

  const review = async (action) => {
    try {
      await api.post(`/invoices/${id}/approval`, { action, comments: reviewComment });
      setReviewComment("");
      toast.success("Action recorded");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const submitForApproval = async () => {
    try {
      await api.post(`/invoices/${id}/submit`);
      toast.success("Submitted");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };



  const recordPay = async () => {
    try {
      await api.post(`/invoices/${id}/payments`, { ...payment, amount: Number(payment.amount) });
      setPayOpen(false);
      toast.success("Payment recorded");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const cancelInv = async () => {
    try {
      await api.post(`/invoices/${id}/cancel`);
      toast.success("Invoice cancelled");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const deleteInv = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      navigate("/app/invoices");
    } catch (err) { toast.error(extractError(err)); }
  };

  const downloadPdf = async () => {
    try {
      setExportingPdf(true);
      const resp = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-4 no-print">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Invoice</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">{inv.invoice_number}</h1>
          <div className="text-sm text-zinc-600 mt-1">Version {inv.version} · {inv.client_name}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={meta.cls} data-testid="invoice-status-badge">{meta.label}</Badge>
          <Button variant="outline" onClick={downloadPdf} disabled={exportingPdf} data-testid="invoice-print-btn"><Download size={14} className="mr-1" /> {exportingPdf ? "Preparing…" : "Download PDF"}</Button>
          {(inv.status === "draft" || inv.status === "changes_requested" || inv.status === "rejected" || inv.status === "pending_approval") && (user.role === "admin" || user.role === "manager") && (
            <Button
              onClick={() => review("approve")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="invoice-approve-btn"
            >
              <CheckCircle2 size={14} className="mr-1" />
              Approve
            </Button>
          )}
      
          {(inv.status === "approved" || inv.status === "sent") && (user.role === "admin" || user.role === "manager") && (
            <Dialog open={payOpen} onOpenChange={setPayOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full" data-testid="invoice-record-payment-btn"><Receipt size={14} className="mr-1" /> Record payment</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Amount</Label><Input type="number" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                  <div><Label>Method</Label>
                    <Select value={payment.method} onValueChange={(v) => setPayment({ ...payment, method: v })}>
                      <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Reference</Label><Input value={payment.reference} onChange={(e) => setPayment({ ...payment, reference: e.target.value })} /></div>
                  <div><Label>Date</Label><Input type="date" value={payment.payment_date} onChange={(e) => setPayment({ ...payment, payment_date: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
                  <Button onClick={recordPay} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="payment-save-btn">Save payment</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isAdmin && inv.status !== "cancelled" && inv.status !== "paid" && (
            <Button variant="ghost" className="text-red-700 hover:bg-red-50" onClick={cancelInv} data-testid="invoice-cancel-btn">Cancel invoice</Button>
          )}
          {isAdmin && (inv.status === "draft" || inv.status === "cancelled") && (
            <Button variant="ghost" className="text-red-700 hover:bg-red-50" onClick={deleteInv} data-testid="invoice-delete-btn">Delete invoice</Button>
          )}
        </div>
      </div>

      <PublicLinkPanel inv={inv} onChange={load} />

      <Tabs defaultValue="invoice" className="no-print">
        <TabsList>
          <TabsTrigger value="invoice" data-testid="tab-invoice">Invoice</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({inv.history?.length || 0})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments ({inv.payments?.length || 0})</TabsTrigger>
        </TabsList>

<TabsContent value="invoice" className="mt-6" forceMount>
  <InvoicePrint inv={inv} company={company} symbol={symbol} />
</TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardContent className="p-0">
              <ul className="divide-y divide-black/5">
                {(inv.history || []).slice().reverse().map((h, i) => (
                  <li key={i} className="p-4">
                    <div className="text-sm capitalize">{h.action.replace("_", " ")} <span className="text-zinc-500">v{h.version}</span></div>
                    <div className="text-xs text-zinc-500">{h.by} · {new Date(h.at).toLocaleString()}</div>
                    {h.comments && <div className="text-sm mt-1 text-zinc-700">"{h.comments}"</div>}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardContent className="p-0">
              {(inv.payments || []).length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500">No payments yet.</div>
              ) : (
                <ul className="divide-y divide-black/5">
                  {inv.payments.map((p, i) => (
                    <li key={i} className="p-4 flex justify-between" data-testid={`payment-row-${i}`}>
                      <div>
                        <div className="text-sm font-medium">{symbol}{p.amount.toLocaleString()} · {p.method}</div>
                        <div className="text-xs text-zinc-500">{p.payment_date} · {p.reference || "—"}</div>
                      </div>
                      <div className="text-xs text-zinc-500">by {p.recorded_by}</div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}

function InvoicePrint({ inv, company, symbol }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [logoError, setLogoError] = React.useState(false);
  const showLogo = !logoError && inv.show_invoice_logo !== false && (inv.company?.logo_file_id || company?.logo_file_id);
  const logoId = inv.company?.logo_file_id || company?.logo_file_id;
  const customs = (inv.visible_custom_fields || []).map((f) => ({ ...f, value: inv.booking_custom_data?.[f.name] }))
    .filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
  return (
    <div id="invoice-pdf-root" className="mx-auto w-full max-w-[794px] bg-white border border-black/10 rounded-md p-6 sm:p-8 print-area" style={{ fontFamily: "Inter, system-ui, sans-serif", lineHeight: 1.45 }} data-testid="invoice-print-area">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 pb-8 border-b border-black/10">
        <div>
          {showLogo && logoId && (
            <img
              src={`${BACKEND_URL}/api/files/${logoId}`}
              alt=""
              className="h-12 mb-3 object-contain"
              data-testid="invoice-logo"
              onError={() => setLogoError(true)}
            />
          )}
          <div className="font-serif text-3xl tracking-tighter">{company?.name || "Company"}</div>
          {company?.address && <div className="text-sm text-zinc-600 whitespace-pre-line mt-1">{company.address}</div>}
          <div className="text-sm text-zinc-600 mt-1">{company?.phone} · {company?.email}</div>
          {company?.gst_number && <div className="text-xs text-zinc-500 mt-1">GST: {company.gst_number}</div>}
        </div>
        <div className="sm:text-right">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Invoice</div>
          <div className="font-serif text-3xl tracking-tighter">{inv.invoice_number}</div>
          <div className="text-sm text-zinc-600 mt-1">Date: {(inv.invoice_date || "").slice(0, 10)}</div>
          {inv.due_date && <div className="text-sm text-zinc-600">Due: {inv.due_date}</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Bill to</div>
          <div className="mt-2 font-medium">{inv.client_name}</div>
          <div className="text-sm text-zinc-600">{inv.client_phone}</div>
          {inv.client_email && <div className="text-sm text-zinc-600">{inv.client_email}</div>}
          {inv.client_address && <div className="text-sm text-zinc-600 mt-1 whitespace-pre-line">{inv.client_address}</div>}
        </div>
        {inv.booking_event_date && (
          <div className="md:text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Event</div>
            <div className="mt-2">{inv.booking_event_type_name}</div>
            <div className="text-sm text-zinc-600">{inv.booking_event_date}</div>
          </div>
        )}
      </div>

      <table className="w-full mt-8 text-sm border-collapse">
        <thead>
          <tr className="text-xs uppercase tracking-[0.18em] text-zinc-500 border-b border-black/10">
            <th className="text-left py-2 font-medium">Description</th>
            <th className="text-right py-2 font-medium">Qty</th>
            <th className="text-right py-2 font-medium">Rate</th>
            <th className="text-right py-2 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {inv.line_items.map((li, i) => (
            <tr key={i} className="border-b border-black/5">
              <td className="py-3">{li.description}</td>
              <td className="text-right py-3">{li.quantity}</td>
              <td className="text-right py-3">{symbol}{li.rate}</td>
              <td className="text-right py-3">{symbol}{((li.quantity || 0) * (li.rate || 0)).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6 flex justify-end">
        <dl className="space-y-1 text-sm w-full md:w-72">
          <Row k="Subtotal" v={`${symbol}${inv.subtotal?.toFixed(2)}`} />
          {inv.discount_amount > 0 && <Row k="Discount" v={`-${symbol}${inv.discount_amount?.toFixed(2)}`} />}
          <Row k={`Tax (${inv.tax_rate}%)`} v={`${symbol}${inv.tax_amount?.toFixed(2)}`} />
          <div className="flex justify-between border-t border-black/10 pt-2 mt-2">
            <span className="font-serif text-xl">Total</span>
            <span className="font-serif text-xl">{symbol}{inv.total?.toFixed(2)}</span>
          </div>
          {inv.amount_paid > 0 && (
            <>
              <Row k="Amount paid" v={`${symbol}${inv.amount_paid?.toFixed(2)}`} />
              <Row k="Balance due" v={`${symbol}${(inv.total - inv.amount_paid).toFixed(2)}`} />
            </>
          )}
        </dl>
      </div>

      {inv.payment_terms && <div className="mt-8 text-sm"><span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Payment terms</span><div className="mt-1">{inv.payment_terms}</div></div>}
      {inv.notes && <div className="mt-6 text-sm"><span className="text-xs uppercase tracking-[0.18em] text-zinc-500">Notes</span><div className="mt-1">{inv.notes}</div></div>}

      {customs.length > 0 && (
        <div className="mt-8 pt-6 border-t border-black/10">
          <span className="block uppercase tracking-[0.18em] text-zinc-500 mb-3 text-xs">Event details</span>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {customs.map((c) => (
              <div key={c.name} data-testid={`invoice-custom-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <dt className="text-xs uppercase tracking-[0.16em] text-zinc-500">{c.name}</dt>
              <dd className="mt-0.5">
                {typeof c.value === "boolean"
                  ? c.value ? "Yes" : "No"
                  : typeof c.value === "object" && c.value?.filename
                  ? c.value.filename
                  : String(c.value)}
              </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {inv.show_tnc && inv.terms_text && (
        <div className="mt-8 pt-6 border-t border-black/10 text-xs text-zinc-600 whitespace-pre-line">
          <span className="block uppercase tracking-[0.18em] text-zinc-500 mb-1">Terms & conditions</span>
          {inv.terms_text}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-zinc-600">{k}</span><span>{v}</span></div>;
}

function PublicLinkPanel({ inv, onChange }) {
  const eligible = ["approved", "sent", "paid","pending_approval","draft","changes_requested"].includes(inv.status);
  const url = inv.public_token ? `${window.location.origin}/invoice/public/${inv.public_token}` : "";
  const [busy, setBusy] = React.useState(false);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await api.post(`/invoices/${inv.id}/public?enabled=${enabled ? "true" : "false"}`);
      toast.success(enabled ? "Public link enabled" : "Public link disabled");
      onChange?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Long-press to copy.");
    }
  };

  return (
    <div className="bg-white border border-black/10 rounded-md p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 no-print" data-testid="invoice-public-panel">
      <div className="flex-1">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Public share link</div>
        <div className="mt-1 text-sm">
          {!eligible ? (
            <span className="text-zinc-600">Public Share link not available.</span>
          ) : inv.public_enabled ? (
            <code className="font-mono text-xs bg-zinc-50 border border-black/10 px-2 py-1 rounded break-all" data-testid="invoice-public-link">{url}</code>
          ) : (
            <span className="text-zinc-600">Link is currently disabled. Enable to let your client view this invoice without logging in.</span>
          )}
        </div>
      </div>
      {eligible && (
        <div className="flex items-center gap-2">
          {inv.public_enabled && (
            <Button variant="outline" onClick={copy} data-testid="invoice-public-copy-btn">Copy link</Button>
          )}
          <Button
            disabled={busy}
            variant={inv.public_enabled ? "outline" : "default"}
            className={inv.public_enabled ? "border-red-200 text-red-700 hover:bg-red-50" : "bg-[#D85C38] hover:bg-[#B84A2A] text-white"}
            onClick={() => toggle(!inv.public_enabled)}
            data-testid="invoice-public-toggle-btn"
          >
            {inv.public_enabled ? "Disable" : "Enable public link"}
          </Button>
        </div>
      )}
    </div>
  );
}
